import {blobToArrayBuffer} from './blob';

export type ImageHeaderSize = {
  width: number;
  height: number;
};

export const HEADER_BYTES = 65536;
export const LOCAL_PREVIEW_MAX_EDGE = 10000;
export const LOCAL_PREVIEW_MAX_PIXELS = 40_000_000;

const LOCAL_PREVIEW_EXTENSIONS = ['png', 'jpg', 'jpeg'];

const isSofMarker = (marker: number): boolean =>
  (marker >= 0xc0 && marker <= 0xc3) ||
  (marker >= 0xc5 && marker <= 0xc7) ||
  (marker >= 0xc9 && marker <= 0xcb) ||
  (marker >= 0xcd && marker <= 0xcf);

const readPngSize = (view: DataView): ImageHeaderSize | null => {
  if (view.byteLength < 24) {
    return null;
  }

  if (
    view.getUint32(0) !== 0x89504e47 ||
    view.getUint32(4) !== 0x0d0a1a0a ||
    view.getUint32(12) !== 0x49484452
  ) {
    return null;
  }

  return {width: view.getUint32(16), height: view.getUint32(20)};
};

const readJpegSize = (view: DataView): ImageHeaderSize | null => {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
    return null;
  }

  let offset = 2;

  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset++;
      continue;
    }

    const marker = view.getUint8(offset + 1);

    if (marker === 0xff) {
      offset++;
      continue;
    }

    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2;
      continue;
    }

    if (marker === 0xd9 || marker === 0xda) {
      return null;
    }

    const length = view.getUint16(offset + 2);

    if (length < 2) {
      return null;
    }

    if (isSofMarker(marker)) {
      if (offset + 9 > view.byteLength) {
        return null;
      }

      return {
        width: view.getUint16(offset + 7),
        height: view.getUint16(offset + 5),
      };
    }

    offset += 2 + length;
  }

  return null;
};

export const readImageHeaderSize = async (
  file: File
): Promise<ImageHeaderSize | null> => {
  try {
    const buffer = await blobToArrayBuffer(file.slice(0, HEADER_BYTES));
    const view = new DataView(buffer);

    return readPngSize(view) ?? readJpegSize(view);
  } catch (error) {
    return null;
  }
};

export const getFileExtension = (fileName: string): string =>
  (fileName.split('.').pop() ?? '').toLowerCase();

export const canPreviewInBrowser = (
  fileName: string,
  size: ImageHeaderSize | null
): boolean => {
  if (!LOCAL_PREVIEW_EXTENSIONS.includes(getFileExtension(fileName))) {
    return false;
  }

  if (!size) {
    return false;
  }

  if (
    size.width > LOCAL_PREVIEW_MAX_EDGE ||
    size.height > LOCAL_PREVIEW_MAX_EDGE
  ) {
    return false;
  }

  return size.width * size.height <= LOCAL_PREVIEW_MAX_PIXELS;
};
